/**
 * Chord Track Component - Logic Pro Inspired
 * Global track that displays and manages project-wide chord progressions
 * Shows chord symbols above the timeline that sync with playback
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Music,
  Plus,
  Trash2,
  Edit3,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Copy,
  Scissors,
} from 'lucide-react';
import { NOTES, CHORD_TYPES, getChordSymbol, buildChord } from '../chordwizard/ChordGenerator';

// Default chord region colors
const CHORD_COLORS = [
  'bg-indigo-500/30 border-indigo-500/50',
  'bg-purple-500/30 border-purple-500/50',
  'bg-blue-500/30 border-blue-500/50',
  'bg-cyan-500/30 border-cyan-500/50',
  'bg-emerald-500/30 border-emerald-500/50',
  'bg-amber-500/30 border-amber-500/50',
  'bg-rose-500/30 border-rose-500/50',
  'bg-pink-500/30 border-pink-500/50',
];

/**
 * Create a new chord region
 */
export const createChordRegion = (startTime, duration, root = 'C', type = 'major') => ({
  id: `chord-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  startTime,
  duration,
  root,
  type,
  symbol: getChordSymbol(root, type),
  notes: buildChord(root, type, 4, 0),
  colorIndex: Math.floor(Math.random() * CHORD_COLORS.length),
});

/**
 * Individual Chord Region Component
 */
const ChordRegion = ({
  chord,
  zoom,
  pixelsPerSecond,
  isSelected,
  isPlaying,
  onClick,
  onDoubleClick,
  onDragStart,
  onResize,
  onDelete,
}) => {
  const regionRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const width = chord.duration * pixelsPerSecond * zoom;
  const left = chord.startTime * pixelsPerSecond * zoom;

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) return;
    e.stopPropagation();
    onClick(chord.id);
    setIsDragging(true);
    setDragOffset(e.clientX - regionRef.current.getBoundingClientRect().left);
    onDragStart(chord.id, e.clientX);
  };

  const handleResizeMouseDown = (e, edge) => {
    e.stopPropagation();
    setIsResizing(edge);
    onClick(chord.id);
  };

  return (
    <div
      ref={regionRef}
      className={`absolute top-1 bottom-1 rounded-md border cursor-pointer
        transition-all duration-150 flex items-center justify-center
        ${CHORD_COLORS[chord.colorIndex % CHORD_COLORS.length]}
        ${isSelected ? 'ring-2 ring-white/50 shadow-lg' : ''}
        ${isPlaying ? 'ring-2 ring-amber-400 shadow-amber-400/30' : ''}
      `}
      style={{
        left: `${left}px`,
        width: `${Math.max(width, 40)}px`,
        minWidth: '40px',
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={() => onDoubleClick(chord)}
      data-testid={`chord-region-${chord.id}`}
    >
      {/* Left resize handle */}
      <div
        className="resize-handle absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize
          hover:bg-white/20 transition-colors"
        onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
      />
      
      {/* Chord Symbol */}
      <div className="flex flex-col items-center px-2 select-none">
        <span className="text-sm font-bold text-white drop-shadow-md">
          {chord.symbol}
        </span>
        <span className="text-[10px] text-white/60 font-mono">
          {chord.duration.toFixed(1)}s
        </span>
      </div>
      
      {/* Right resize handle */}
      <div
        className="resize-handle absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize
          hover:bg-white/20 transition-colors"
        onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
      />
      
      {/* Delete button on hover when selected */}
      {isSelected && (
        <button
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 
            flex items-center justify-center hover:bg-red-400 transition-colors
            shadow-lg"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(chord.id);
          }}
        >
          <Trash2 className="w-3 h-3 text-white" />
        </button>
      )}
    </div>
  );
};

/**
 * Chord Editor Modal
 */
const ChordEditor = ({ chord, onSave, onClose }) => {
  const [root, setRoot] = useState(chord?.root || 'C');
  const [type, setType] = useState(chord?.type || 'major');
  const [duration, setDuration] = useState(chord?.duration || 2);

  const chordTypes = Object.keys(CHORD_TYPES);

  const handleSave = () => {
    onSave({
      ...chord,
      root,
      type,
      duration,
      symbol: getChordSymbol(root, type),
      notes: buildChord(root, type, 4, 0),
    });
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="absolute z-50 bg-zinc-900 border border-zinc-700 rounded-lg p-4 shadow-xl
        min-w-[280px]"
      style={{ top: '100%', left: '50%', transform: 'translateX(-50%)' }}
    >
      <h4 className="text-sm font-semibold text-white mb-3">Edit Chord</h4>
      
      {/* Root Note Selection */}
      <div className="mb-3">
        <label className="text-xs text-zinc-400 mb-1 block">Root Note</label>
        <div className="grid grid-cols-6 gap-1">
          {NOTES.map((note) => (
            <button
              key={note}
              onClick={() => setRoot(note)}
              className={`px-2 py-1.5 text-xs rounded transition-colors
                ${root === note 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                }`}
            >
              {note}
            </button>
          ))}
        </div>
      </div>
      
      {/* Chord Type Selection */}
      <div className="mb-3">
        <label className="text-xs text-zinc-400 mb-1 block">Chord Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm text-white"
        >
          {chordTypes.map((t) => (
            <option key={t} value={t}>
              {getChordSymbol(root, t)}
            </option>
          ))}
        </select>
      </div>
      
      {/* Duration */}
      <div className="mb-4">
        <label className="text-xs text-zinc-400 mb-1 block">Duration (seconds)</label>
        <input
          type="number"
          value={duration}
          onChange={(e) => setDuration(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
          step="0.5"
          min="0.5"
          className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-sm text-white"
        />
      </div>
      
      {/* Preview */}
      <div className="mb-4 p-2 bg-zinc-800 rounded text-center">
        <span className="text-lg font-bold text-white">{getChordSymbol(root, type)}</span>
      </div>
      
      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onClose}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          className="flex-1 bg-indigo-500 hover:bg-indigo-600"
        >
          Save
        </Button>
      </div>
    </motion.div>
  );
};

/**
 * Main Chord Track Component
 */
export const ChordTrack = ({
  chords = [],
  onChordsChange,
  currentTime = 0,
  isPlaying = false,
  zoom = 1,
  totalDuration = 60,
  bpm = 120,
  snapEnabled = true,
  onChordPlay,
}) => {
  const trackRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedChordId, setSelectedChordId] = useState(null);
  const [editingChord, setEditingChord] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  
  const pixelsPerSecond = 50; // Base pixels per second
  const trackWidth = totalDuration * pixelsPerSecond * zoom;
  const secondsPerBeat = 60 / bpm;

  // Find current chord being played
  const currentChord = chords.find(
    (c) => currentTime >= c.startTime && currentTime < c.startTime + c.duration
  );

  // Snap time to grid
  const snapToGrid = useCallback((time) => {
    if (!snapEnabled) return time;
    const snapInterval = secondsPerBeat; // Snap to beats
    return Math.round(time / snapInterval) * snapInterval;
  }, [snapEnabled, secondsPerBeat]);

  // Handle double-click to add new chord
  const handleDoubleClick = (e) => {
    if (e.target !== trackRef.current) return;
    
    const rect = trackRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + trackRef.current.scrollLeft;
    const time = snapToGrid(x / (pixelsPerSecond * zoom));
    
    // Check for overlapping chords
    const hasOverlap = chords.some(
      (c) => time >= c.startTime && time < c.startTime + c.duration
    );
    
    if (!hasOverlap) {
      const newChord = createChordRegion(time, secondsPerBeat * 4, 'C', 'major');
      onChordsChange([...chords, newChord]);
      setSelectedChordId(newChord.id);
    }
  };

  // Handle chord drag
  const handleDragStart = (chordId, clientX) => {
    const chord = chords.find((c) => c.id === chordId);
    if (chord) {
      setIsDragging(true);
      setDragStartX(clientX);
      setDragStartTime(chord.startTime);
    }
  };

  // Handle mouse move for dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - dragStartX;
      const deltaTime = deltaX / (pixelsPerSecond * zoom);
      const newTime = snapToGrid(Math.max(0, dragStartTime + deltaTime));
      
      const updatedChords = chords.map((c) =>
        c.id === selectedChordId ? { ...c, startTime: newTime } : c
      );
      onChordsChange(updatedChords);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStartX, dragStartTime, selectedChordId, chords, onChordsChange, zoom, snapToGrid]);

  // Handle chord update from editor
  const handleChordUpdate = (updatedChord) => {
    const updatedChords = chords.map((c) =>
      c.id === updatedChord.id ? updatedChord : c
    );
    onChordsChange(updatedChords);
  };

  // Handle chord deletion
  const handleDeleteChord = (chordId) => {
    onChordsChange(chords.filter((c) => c.id !== chordId));
    setSelectedChordId(null);
  };

  // Handle click on track (deselect)
  const handleTrackClick = (e) => {
    if (e.target === trackRef.current) {
      setSelectedChordId(null);
      setEditingChord(null);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedChordId) return;
      
      if (e.key === 'Delete' || e.key === 'Backspace') {
        handleDeleteChord(selectedChordId);
      } else if (e.key === 'Escape') {
        setSelectedChordId(null);
        setEditingChord(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedChordId]);

  return (
    <div className="bg-zinc-900/50 border-b border-zinc-800" data-testid="chord-track">
      {/* Track Header */}
      <div className="flex items-center px-3 py-2 bg-zinc-900/80 border-b border-zinc-800/50">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mr-2 text-zinc-400 hover:text-white transition-colors"
        >
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
        
        <Music className="w-4 h-4 text-indigo-400 mr-2" />
        <span className="text-sm font-medium text-white">Chord Track</span>
        
        {currentChord && (
          <span className="ml-3 px-2 py-0.5 bg-indigo-500/30 rounded text-xs text-indigo-300 font-mono">
            {currentChord.symbol}
          </span>
        )}
        
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newChord = createChordRegion(
                snapToGrid(currentTime),
                secondsPerBeat * 4,
                'C',
                'major'
              );
              onChordsChange([...chords, newChord]);
              setEditingChord(newChord);
            }}
            className="h-7 px-2 text-zinc-400 hover:text-white"
            data-testid="add-chord-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add
          </Button>
        </div>
      </div>
      
      {/* Track Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 48, opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="relative overflow-hidden"
          >
            <div
              ref={trackRef}
              className="h-full overflow-x-auto relative"
              style={{ width: `${trackWidth}px` }}
              onClick={handleTrackClick}
              onDoubleClick={handleDoubleClick}
            >
              {/* Grid lines */}
              <div className="absolute inset-0 pointer-events-none">
                {Array.from({ length: Math.ceil(totalDuration / secondsPerBeat) }, (_, i) => (
                  <div
                    key={i}
                    className={`absolute top-0 bottom-0 ${
                      i % 4 === 0 ? 'border-l border-zinc-700/50' : 'border-l border-zinc-800/30'
                    }`}
                    style={{ left: `${i * secondsPerBeat * pixelsPerSecond * zoom}px` }}
                  />
                ))}
              </div>
              
              {/* Chord Regions */}
              {chords.map((chord) => (
                <ChordRegion
                  key={chord.id}
                  chord={chord}
                  zoom={zoom}
                  pixelsPerSecond={pixelsPerSecond}
                  isSelected={selectedChordId === chord.id}
                  isPlaying={currentChord?.id === chord.id && isPlaying}
                  onClick={setSelectedChordId}
                  onDoubleClick={setEditingChord}
                  onDragStart={handleDragStart}
                  onDelete={handleDeleteChord}
                />
              ))}
              
              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-cyan-400 pointer-events-none z-10"
                style={{ left: `${currentTime * pixelsPerSecond * zoom}px` }}
              />
            </div>
            
            {/* Chord Editor Popup */}
            <AnimatePresence>
              {editingChord && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-50">
                  <ChordEditor
                    chord={editingChord}
                    onSave={handleChordUpdate}
                    onClose={() => setEditingChord(null)}
                  />
                </div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChordTrack;
